import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { User, MapPin, Plus, Factory, FileText, AlertTriangle, HardHat, Shield, CheckCircle2, XCircle, Clock, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { useTier } from '../context/TierContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

const OperatorEditDrawer = ({
  open,
  onOpenChange,
  operator,
  suppliers,
  equipmentTypes,
  workerTypes = [],
  onSubmit,
  onSupplierCreate,
  defaultSupplierId = null
}) => {
  const { hasFeature } = useTier();
  const { api } = useAuth();
  const hasQuotes = hasFeature('quotes');
  const hasTimesheets = hasFeature('timesheets');

  // EW2-286 — per-customer compliance status
  const [ccCustomers, setCcCustomers] = useState([]);
  const [ccSelected, setCcSelected] = useState('');
  const [ccStatus, setCcStatus] = useState(null);
  const [ccLoading, setCcLoading] = useState(false);

  // Mandatory compliance status (read-only — system-wide mandatory types)
  const [mandatoryStatus, setMandatoryStatus] = useState(null);
  const [mandatoryLoading, setMandatoryLoading] = useState(false);

  useEffect(() => {
    if (!open || !operator?.id) { setCcCustomers([]); setCcSelected(''); setCcStatus(null); return; }
    (async () => {
      try {
        const res = await api.get('/customers');
        const list = (Array.isArray(res.data) ? res.data : (res.data?.items || []))
          .filter(c => (c.required_compliance_type_ids?.length || c.preferred_compliance_type_ids?.length));
        setCcCustomers(list);
      } catch (e) { setCcCustomers([]); }
    })();
  }, [open, operator?.id, api]);

  // Load mandatory compliance status when drawer opens
  useEffect(() => {
    if (!open || !operator?.id) { setMandatoryStatus(null); return; }
    setMandatoryLoading(true);
    (async () => {
      try {
        const res = await api.get(`/compliance/mandatory-status?operator_id=${operator.id}`);
        const opStatus = res.data?.operators?.[operator.id];
        setMandatoryStatus(opStatus || null);
      } catch (e) { setMandatoryStatus(null); }
      finally { setMandatoryLoading(false); }
    })();
  }, [open, operator?.id, api]);

  const checkCustomerCompliance = async (customerId) => {
    setCcSelected(customerId);
    setCcStatus(null);
    if (!customerId || !operator?.id) return;
    setCcLoading(true);
    try {
      const res = await api.get(`/operators/${operator.id}/compliance-status`, { params: { customer_id: customerId } });
      setCcStatus(res.data);
    } catch (e) { setCcStatus(null); }
    finally { setCcLoading(false); }
  };

  const [form, setForm] = useState({
    name: '',
    supplier_id: '',
    role: 'operator',
    equipment_types: [],
    worker_type_ids: [],
    is_available: true,
    is_active: true,
    phone: '',
    email: '',
    suburb: '',
    postcode: '',
    state: '',
    mobile_enabled: false,
    mobile_pin: '',
    operator_code: '',
    mobile_licence_type: 'full',
    mobile_quotes_enabled: false,
    is_yard_dispatcher: false,
    notification_preference: 'email',
    employment_status: ''
  });

  // Quick Add Supplier Dialog
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [supplierForm, setSupplierForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: ''
  });
  
  // Email warning dialog
  const [emailWarningOpen, setEmailWarningOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  useEffect(() => {
    if (operator) {
      setForm({
        name: operator.name || '',
        supplier_id: operator.supplier_id || '',
        role: operator.role || 'operator',
        equipment_types: operator.equipment_types || [],
        worker_type_ids: operator.worker_type_ids || [],
        is_available: operator.is_available !== false,
        is_active: operator.is_active !== false,
        phone: operator.phone || '',
        email: operator.email || '',
        suburb: operator.suburb || '',
        postcode: operator.postcode || '',
        state: operator.state || '',
        mobile_enabled: operator.mobile_enabled || false,
        mobile_pin: operator.mobile_pin || '',
        operator_code: operator.operator_code || '',
        mobile_licence_type: operator.mobile_licence_type || 'full',
        mobile_quotes_enabled: operator.mobile_quotes_enabled || false,
        is_yard_dispatcher: operator.is_yard_dispatcher || false,
        notification_preference: operator.notification_preference || 'email',
        employment_status: operator.employment_status || ''
      });
    } else {
      setForm({
        name: '',
        supplier_id: defaultSupplierId || '',
        role: 'operator',
        equipment_types: [],
        worker_type_ids: [],
        is_available: true,
    is_active: true,
        phone: '',
        email: '',
        suburb: '',
        postcode: '',
        state: '',
        mobile_enabled: false,
        mobile_pin: '',
        operator_code: '',
        mobile_licence_type: 'full',
        mobile_quotes_enabled: false,
        is_yard_dispatcher: false,
        employment_status: ''
      });
    }
  }, [operator, defaultSupplierId, open]);

  const handleSupplierSelect = (value) => {
    if (value === '__add_new__') {
      setSupplierForm({ company_name: '', contact_name: '', phone: '', email: '' });
      setSupplierDialogOpen(true);
    } else {
      setForm({ ...form, supplier_id: value });
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (onSupplierCreate) {
      const newSupplier = await onSupplierCreate(supplierForm);
      if (newSupplier) {
        setForm({ ...form, supplier_id: newSupplier.id });
        setSupplierDialogOpen(false);
      }
    }
  };

  const toggleEquipmentType = (type) => {
    setForm(prev => ({
      ...prev,
      equipment_types: prev.equipment_types.includes(type)
        ? prev.equipment_types.filter(t => t !== type)
        : [...prev.equipment_types, type]
    }));
  };

  const toggleAllEquipmentTypes = () => {
    const allTypeNames = equipmentTypes.map(et => et.name);
    setForm(prev => {
      const allSelected = allTypeNames.every(name => prev.equipment_types.includes(name));
      return {
        ...prev,
        equipment_types: allSelected ? [] : allTypeNames
      };
    });
  };

  const toggleWorkerType = (workerTypeId) => {
    setForm(prev => ({
      ...prev,
      worker_type_ids: prev.worker_type_ids.includes(workerTypeId)
        ? prev.worker_type_ids.filter(id => id !== workerTypeId)
        : [...prev.worker_type_ids, workerTypeId]
    }));
  };

  const toggleAllWorkerTypes = () => {
    const allWorkerTypeIds = workerTypes.map(wt => wt.id);
    setForm(prev => {
      const allSelected = allWorkerTypeIds.every(id => prev.worker_type_ids.includes(id));
      return {
        ...prev,
        worker_type_ids: allSelected ? [] : allWorkerTypeIds
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.supplier_id) {
      return; // Don't submit without supplier
    }
    
    // Capture form data immediately to avoid any race conditions
    const formDataToSubmit = { ...form };
    
    // Check if mobile is enabled but no valid email
    const isValidEmail = formDataToSubmit.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formDataToSubmit.email);
    if (formDataToSubmit.mobile_enabled && !isValidEmail) {
      // Show warning dialog
      setPendingSubmit({ form: formDataToSubmit, operator });
      setEmailWarningOpen(true);
      return;
    }
    
    onSubmit(formDataToSubmit, operator);
  };
  
  const handleConfirmWithoutEmail = () => {
    if (pendingSubmit) {
      onSubmit(pendingSubmit.form, pendingSubmit.operator);
    }
    setEmailWarningOpen(false);
    setPendingSubmit(null);
  };
  
  const handleCancelEmailWarning = () => {
    setEmailWarningOpen(false);
    setPendingSubmit(null);
  };

  const isEditing = !!operator;
  const isFormValid = form.name && form.supplier_id;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <User className="h-5 w-5 text-green-500" />
              {isEditing ? 'Edit Worker' : 'Add New Worker'}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4" data-testid="operator-form">
            <div className="space-y-2">
              <Label className="label-overline">Supplier (Employer) <span className="text-red-500">*</span></Label>
              <Select value={form.supplier_id || ''} onValueChange={handleSupplierSelect}>
                <SelectTrigger data-testid="operator-supplier-select">
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {onSupplierCreate && (
                    <>
                      <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                        <span className="flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          ADD NEW SUPPLIER
                        </span>
                      </SelectItem>
                      {suppliers.length > 0 && <div className="border-t border-border my-1" />}
                    </>
                  )}
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {suppliers.length === 0 && (
                <p className="text-xs text-amber-500">
                  No suppliers found. Please add a supplier first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Full Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="operator-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="John Smith"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(value) => setForm({ ...form, role: value })}
                >
                  <SelectTrigger data-testid="operator-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Operator</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Employment Status</Label>
                <Select
                  value={form.employment_status || ''}
                  onValueChange={(value) => setForm({ ...form, employment_status: value || '' })}
                >
                  <SelectTrigger data-testid="operator-employment-status-select">
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Available</Label>
                <div className="flex items-center h-10">
                  <Switch
                    checked={form.is_available}
                    onCheckedChange={(checked) => setForm({ ...form, is_available: checked })}
                    data-testid="operator-available-switch"
                  />
                  <span className="ml-2 text-sm">
                    {form.is_available ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Status</Label>
                <div className="flex items-center h-10">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                    data-testid="operator-active-switch"
                  />
                  <span className={`ml-2 text-sm ${form.is_active ? '' : 'text-red-400'}`}>
                    {form.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input
                  data-testid="operator-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 234 567 890"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  data-testid="operator-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="john@email.com"
                />
              </div>
            </div>

            {/* Notification delivery preference */}
            <div className="space-y-2">
              <Label className="label-overline">Job notification delivery</Label>
              <Select
                value={form.notification_preference || 'email'}
                onValueChange={(v) => setForm({ ...form, notification_preference: v })}
              >
                <SelectTrigger data-testid="operator-notification-pref-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="push">Push (mobile app)</SelectItem>
                  <SelectItem value="both">Email + Push</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">How this worker receives "Notify Workers" job details.</p>
            </div>

            {/* Location Section */}
            <div className="space-y-2">
              <Label className="label-overline flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Location (for smart scheduling)
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <Input
                    value={form.suburb}
                    onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                    placeholder="Suburb"
                  />
                </div>
                <div>
                  <Input
                    value={form.postcode}
                    onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                    placeholder="Postcode"
                    maxLength={4}
                  />
                </div>
                <div>
                  <Select
                    value={form.state}
                    onValueChange={(value) => setForm({ ...form, state: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUSTRALIAN_STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-overline">Can Operate</Label>
                {equipmentTypes.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground hover:text-foreground"
                    onClick={toggleAllEquipmentTypes}
                  >
                    {equipmentTypes.every(et => form.equipment_types.includes(et.name)) ? 'Clear All' : 'Select All'}
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {equipmentTypes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No asset types available. Add them in Data Management.</p>
                ) : (
                  equipmentTypes.map((et) => (
                    <Badge
                      key={et.id || et.name}
                      variant={form.equipment_types.includes(et.name) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-colors ${
                        form.equipment_types.includes(et.name) 
                          ? 'bg-[#ffd108] text-black hover:bg-[#E5B93D]' 
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => toggleEquipmentType(et.name)}
                    >
                      {et.name}
                    </Badge>
                  ))
                )}
              </div>
            </div>

            {/* Worker Types Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-overline flex items-center gap-1">
                  <HardHat className="h-3 w-3" />
                  Worker Types
                </Label>
                {workerTypes.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground hover:text-foreground"
                    onClick={toggleAllWorkerTypes}
                  >
                    {workerTypes.every(wt => form.worker_type_ids.includes(wt.id)) ? 'Clear All' : 'Select All'}
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {workerTypes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No worker types available. Add them in Data Management.</p>
                ) : (
                  workerTypes.map((wt) => (
                    <Badge
                      key={wt.id}
                      variant={form.worker_type_ids.includes(wt.id) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-colors ${
                        form.worker_type_ids.includes(wt.id) 
                          ? 'bg-violet-500 text-white hover:bg-violet-600' 
                          : 'hover:bg-muted'
                      }`}
                      style={form.worker_type_ids.includes(wt.id) && wt.color ? { backgroundColor: wt.color } : {}}
                      onClick={() => toggleWorkerType(wt.id)}
                      data-testid={`worker-type-badge-${wt.id}`}
                    >
                      {wt.name}
                    </Badge>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">Select the worker types this operator can perform (e.g., Dogman, Rigger)</p>
            </div>

            {/* Mobile Access Section */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="label-overline">Mobile App Access</Label>
                  <p className="text-xs text-muted-foreground">Allow operator to use the mobile app</p>
                </div>
                <Switch
                  checked={form.mobile_enabled}
                  onCheckedChange={(checked) => {
                    setForm({ ...form, mobile_enabled: checked });
                  }}
                  data-testid="operator-mobile-switch"
                />
              </div>

              {/* Licence Type — PPD always available, timesheet needs tier support */}
              {form.mobile_enabled && (
                <div className="pl-4 border-l-2 border-[#ffd108] space-y-2">
                  <Label className="label-overline">Licence Type</Label>
                  <Select
                    value={form.mobile_licence_type}
                    onValueChange={(value) => setForm({
                      ...form,
                      mobile_licence_type: value,
                      // Timesheet-only and PPD workers can't use full-app extras
                      mobile_quotes_enabled: value === 'full' ? form.mobile_quotes_enabled : false,
                      is_yard_dispatcher: value === 'full' ? form.is_yard_dispatcher : false,
                    })}
                  >
                    <SelectTrigger data-testid="operator-licence-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full" data-testid="licence-type-full">Full — complete mobile app access</SelectItem>
                      {hasTimesheets && <SelectItem value="timesheet" data-testid="licence-type-timesheet">Timesheet only — timesheets &amp; leave</SelectItem>}
                      <SelectItem value="ppd" data-testid="licence-type-ppd">Pay Per Docket — jobs &amp; dockets only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {form.mobile_licence_type === 'timesheet'
                      ? 'This worker can only submit timesheets and leave requests on mobile.'
                      : form.mobile_licence_type === 'ppd'
                        ? 'This worker uses pre-paid credits. One credit per docket submitted. Jobs only — no forms, timesheets, or leave.'
                        : 'Full access to jobs, dockets, schedule and more.'}
                  </p>
                </div>
              )}

              {form.mobile_enabled && (
                <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-[#ffd108]">
                  <div className="space-y-2">
                    <Label className="label-overline">Access Code</Label>
                    <Input
                      value={form.operator_code}
                      onChange={(e) => setForm({ ...form, operator_code: e.target.value.toUpperCase() })}
                      placeholder="e.g. JH001"
                      className="font-mono uppercase"
                      data-testid="operator-code-input"
                    />
                    <p className="text-xs text-muted-foreground">Optional — short identifier</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Access PIN</Label>
                    <Input
                      type="password"
                      value={form.mobile_pin}
                      onChange={(e) => setForm({ ...form, mobile_pin: e.target.value })}
                      placeholder="4-6 digits"
                      maxLength={6}
                      data-testid="operator-pin-input"
                    />
                    <p className="text-xs text-muted-foreground">Optional — for legacy access</p>
                  </div>
                </div>
              )}

              {/* Quoting Access - Only show if tenant has quotes feature and mobile is enabled (full licence only) */}
              {form.mobile_enabled && hasQuotes && form.mobile_licence_type === 'full' && (
                <div className="pl-4 border-l-2 border-[#ffd108]">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <Label className="label-overline">Quoting Access</Label>
                        <p className="text-xs text-muted-foreground">Allow creating and managing quotes on mobile</p>
                      </div>
                    </div>
                    <Switch
                      checked={form.mobile_quotes_enabled}
                      onCheckedChange={(checked) => setForm({ ...form, mobile_quotes_enabled: checked })}
                      data-testid="operator-mobile-quotes-switch"
                    />
                  </div>
                </div>
              )}

              {/* Yard Dispatcher — unlocks the mobile Inspections tab
                  for pre/post dry-hire inspections. (full licence only) */}
              {form.mobile_enabled && form.mobile_licence_type === 'full' && (
                <div className="pl-4 border-l-2 border-[#ffd108]">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-500/20 rounded-lg">
                        <FileText className="h-4 w-4 text-amber-500" />
                      </div>
                      <div>
                        <Label className="label-overline">Yard Dispatcher</Label>
                        <p className="text-xs text-muted-foreground">Can perform dry-hire pre/post inspections on mobile</p>
                      </div>
                    </div>
                    <Switch
                      checked={form.is_yard_dispatcher}
                      onCheckedChange={(checked) => setForm({ ...form, is_yard_dispatcher: checked })}
                      data-testid="operator-yard-dispatcher-switch"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Mandatory Compliance Status (read-only, system-wide) */}
            {operator?.id && (
              <div className="space-y-3 p-3 rounded-lg border border-border/60" data-testid="worker-mandatory-compliance">
                <Label className="label-overline flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#ffd108]" />
                  Onboarding Compliance
                </Label>
                <p className="text-xs text-muted-foreground">
                  System-wide mandatory compliance types. Workers cannot be allocated to any job until all are met.
                  Manage mandatory types in Data Management → Compliance Types.
                </p>
                {mandatoryLoading ? (
                  <p className="text-xs text-muted-foreground py-2">Loading compliance status…</p>
                ) : !mandatoryStatus || mandatoryStatus.total === 0 ? (
                  <div className="text-center py-3 text-xs text-muted-foreground border border-dashed rounded-lg">
                    No mandatory compliance types configured.
                    Set them under Data Management → Compliance Types.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {mandatoryStatus.fully_compliant ? (
                        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-xs gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Fully Onboarded
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/15 text-red-500 border-red-500/30 text-xs gap-1">
                          <XCircle className="h-3 w-3" /> {mandatoryStatus.met}/{mandatoryStatus.total} Complete
                        </Badge>
                      )}
                    </div>
                    {mandatoryStatus.types?.map((t) => {
                      const icon = {
                        valid: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />,
                        expiring_soon: <Clock className="h-3.5 w-3.5 text-amber-600" />,
                        expired: <AlertTriangle className="h-3.5 w-3.5 text-red-500" />,
                        missing: <XCircle className="h-3.5 w-3.5 text-zinc-400" />,
                      }[t.status] || <XCircle className="h-3.5 w-3.5 text-zinc-400" />;
                      const statusBg = {
                        valid: 'bg-emerald-500/5 border-emerald-500/20',
                        expiring_soon: 'bg-amber-500/5 border-amber-500/20',
                        expired: 'bg-red-500/5 border-red-500/20',
                        missing: 'bg-zinc-500/5 border-zinc-500/20',
                      }[t.status] || '';
                      const statusLabel = {
                        valid: 'Valid',
                        expiring_soon: 'Expiring Soon',
                        expired: 'Expired',
                        missing: 'Missing',
                      }[t.status] || 'Unknown';
                      return (
                        <div key={t.type_id} className={`flex items-center justify-between rounded-md border p-2 ${statusBg}`} data-testid={`mandatory-type-${t.type_id}`}>
                          <div className="flex items-center gap-2">
                            {icon}
                            <span className="text-sm">{t.type_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {t.expiry_date && <span className="text-[10px] text-muted-foreground font-mono">Exp: {t.expiry_date}</span>}
                            <Badge variant="outline" className="text-[10px]">{statusLabel}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {operator?.id && ccCustomers.length > 0 && (
              <div className="space-y-2 p-3 rounded-lg border border-border/60" data-testid="operator-compliance-check">
                <Label className="label-overline flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#ffd108]" />
                  Customer Compliance Check
                </Label>
                <p className="text-xs text-muted-foreground">See whether this operator meets a customer's mandatory & preferred compliance.</p>
                <Select value={ccSelected} onValueChange={checkCustomerCompliance}>
                  <SelectTrigger data-testid="operator-compliance-customer-select">
                    <SelectValue placeholder="Select a customer to check" />
                  </SelectTrigger>
                  <SelectContent>
                    {ccCustomers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name || c.contact_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ccLoading && <p className="text-xs text-muted-foreground">Checking…</p>}
                {ccStatus && (
                  <div className="space-y-2" data-testid="operator-compliance-result">
                    {ccStatus.status === 'ok' && (
                      <div className="flex items-center gap-2 text-green-600 text-sm" data-testid="operator-compliance-ok">
                        <CheckCircle2 className="h-4 w-4" /> All mandatory requirements met
                      </div>
                    )}
                    {ccStatus.missing_mandatory?.length > 0 && (
                      <div className="space-y-1" data-testid="operator-compliance-blocked">
                        <div className="flex items-center gap-2 text-red-600 text-sm">
                          <XCircle className="h-4 w-4" /> Missing mandatory (allocation blocked)
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {ccStatus.missing_mandatory.map(m => (
                            <Badge key={m.id} variant="destructive" className="text-xs">{m.name}{m.source ? ` · ${m.source}` : ''}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {ccStatus.missing_preferred?.length > 0 && (
                      <div className="space-y-1" data-testid="operator-compliance-warning">
                        <div className="flex items-center gap-2 text-amber-600 text-sm">
                          <AlertTriangle className="h-4 w-4" /> Missing preferred (allowed with warning)
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {ccStatus.missing_preferred.map(m => (
                            <Badge key={m.id} variant="outline" className="text-xs">{m.name}{m.source ? ` · ${m.source}` : ''}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <SheetFooter className="pt-4 border-t mt-6 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
              {!isFormValid && (
                <p className="text-xs text-amber-500 mr-auto" data-testid="operator-form-validation-hint">
                  {!form.name
                    ? 'Enter the worker\u2019s full name to continue.'
                    : 'Select a Supplier (Employer) to continue.'}
                </p>
              )}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="operator-submit-btn" disabled={!isFormValid}>
                {isEditing ? 'Update' : 'Add'} Worker
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Quick Add Supplier Dialog */}
      {onSupplierCreate && (
        <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Factory className="h-5 w-5 text-[#ffd108]" />
                Quick Add Supplier
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSupplier} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Company Name <span className="text-red-500">*</span></Label>
                <Input
                  value={supplierForm.company_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, company_name: e.target.value })}
                  placeholder="ABC Equipment Hire"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Contact Name <span className="text-red-500">*</span></Label>
                <Input
                  value={supplierForm.contact_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Phone</Label>
                  <Input
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    placeholder="+1 234 567 890"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Email</Label>
                  <Input
                    type="email"
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                    placeholder="john@supplier.com"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSupplierDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-volt">
                  Create Supplier
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Email Warning Dialog */}
      <Dialog open={emailWarningOpen} onOpenChange={setEmailWarningOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              No Email Address
            </DialogTitle>
            <DialogDescription>
              This operator doesn't have a valid email address. They won't be able to receive their login details automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-sm">
                Would you like to add an email address so the operator can receive their Operator Code and Access PIN?
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleConfirmWithoutEmail}>
              Continue Without Email
            </Button>
            <Button type="button" className="btn-volt" onClick={handleCancelEmailWarning}>
              Add Email Address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OperatorEditDrawer;
